/**
 * Created: 2026-02-14
 * Initial commit: Add WidgetUtilsContext setup for FileUploader
 *
 * Widget utilities for @homemaster/jslib components like FileUploader.
 */

import { gql } from '@apollo/client'
import type { WidgetUtilsContextType } from '@homemaster/jslib/widget'
import FileIcon from '@mui/icons-material/InsertDriveFileOutlined'
import type { MultipartValueSingle } from 'superagent'
import request from 'superagent'
import { client as apolloClient } from './apollo'
import { getImageUrl } from './config'
import { PrepareOssUploadDocument } from './gql/graphql'

gql`
  mutation PrepareOssUpload($uploadInput: FileUploadInput!) {
    prepareOssUpload(uploadInput: $uploadInput) {
      ... on File {
        name
        url
      }
      ... on FileUploadArgs {
        bucketUrl
        key
        OSSAccessKeyId
        policy
        Signature
        SecurityToken
        callback
      }
    }
  }
`

const widgetUtils: WidgetUtilsContextType = {
  prepareOSSUpload: async (hash, file) => {
    const result = await apolloClient.mutate({
      mutation: PrepareOssUploadDocument,
      variables: {
        uploadInput: {
          hash,
          name: file.name,
          type: file.type || '',
        },
      },
    })

    if (!result.data) {
      throw new Error('No data returned from prepareOssUpload mutation')
    }

    const response = result.data.prepareOssUpload

    // Check if it's a File (already uploaded) or FileUploadArgs (needs upload)
    if (response.__typename === 'File') {
      return {
        file: {
          name: response.name,
          file_name: file.name,
          mime_type: file.type || '',
          size: 0, // Size not available from mutation
          hash: hash,
          bucket: '', // Bucket not available from mutation
          create_time: new Date().toISOString(),
        },
      }
    }

    // It's FileUploadArgs
    return {
      uploadArgs: {
        bucketUrl: response.bucketUrl,
        key: response.key,
        OSSAccessKeyId: response.OSSAccessKeyId,
        policy: response.policy,
        Signature: response.Signature,
        SecurityToken: response.SecurityToken,
        callback: response.callback,
      },
    }
  },

  uploadToOSS: async (file, args, onProgress) => {
    const ret = await request
      .post(args.bucketUrl)
      .on('progress', onProgress)
      .field('key', args.key)
      .field('OSSAccessKeyId', args.OSSAccessKeyId)
      .field('policy', args.policy)
      .field('Signature', args.Signature)
      .field('x-oss-security-token', args.SecurityToken)
      .field('callback', args.callback)
      .attach('file', file as MultipartValueSingle)
    return JSON.parse(ret.text)
  },

  getFileIcon: () => {
    return FileIcon
  },

  getFileUrl: (name) => {
    return getImageUrl(name)
  },

  getThumbUrl: (name) => {
    return `${getImageUrl(name)}?x-oss-process=style/thumb`
  },

  getPreviewUrl: (name) => {
    return `${getImageUrl(name)}?x-oss-process=style/preview`
  },

  uploadFromWX: async (_localId: string) => {
    // WeChat upload not used in this project
    throw new Error('WeChat upload not implemented')
  },

  loadRegion: async () => {
    // Region not used in this project
    throw new Error('Region loading not implemented')
  },
}

export default widgetUtils
